import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { getPlantDetails, uploadSelectedSheet } from "../../network/cipAPI";
import createNotification, {
  ERROR_NOTIFICATION,
} from "../../components/Notifications";
import {
  DEFAULT_RADIO_COLUMNS,
  FILE_SHEET_MAPPING_COLUMN_TYPES,
} from "../../constants/global.constants";
import WPDropdown from "../../packages/WPDropdown";
import { extractPlantId } from "../../utils/Utils";

const FileSheetSelection = React.forwardRef(
  ({
    setFileId,
    setColumnOptions,
    fileInfo,
    selectedTrain,
    setMappedColumn,
    setMappedReference,
    sheetNames,
    setSheetNames,
    selectedSheet,
    setSelectedSheet,
    setIsLoading,
    setSelectedCIPFileName,
    setHasRecurrentCIPs,
    setIsAppendFile,
    setAppendFileList,
    setSelectedRadioColumn,
    setUploadedFiles,
    uploadedFiles = [],
    setColMappingType,
    setFileInfo,
  }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const plantId = extractPlantId();

    const [selectedFileInfo, setSelectedFileInfo] = useState(fileInfo);

    const fileDropdownOptions = uploadedFiles.map((file) => ({
      label: file.file_name,
      value: file.file_name,
    }));

    const sheetDropdownOptions = (sheetNames || []).map((sheetName) => ({
      label: sheetName,
      value: sheetName,
    }));

    const resetState = () => {
      setFileId(null);
      setMappedColumn([]);
      setMappedReference(null);
      setIsLoading(false);
    };

    const handleFileChange = (value) => {
      if (value === selectedFileInfo?.fileName) return;

      const selectedfileInfo = uploadedFiles.find(
        (file) => file.file_name === value,
      );
      const newSelectedFileInfo = {
        fileName: selectedfileInfo.file_name,
        plant_file_id: selectedfileInfo.plant_file_id,
      };
      setSelectedFileInfo(newSelectedFileInfo);

      const sheetNamesArr = selectedfileInfo.sheets
        ? selectedfileInfo.sheets.map((sheetObj) => sheetObj.sheet)
        : [];
      setSheetNames(sheetNamesArr);
      setSelectedSheet(null);
      setMappedColumn([]);
      setMappedReference(null);
      setSelectedCIPFileName(null);
      setHasRecurrentCIPs(false);
      setIsAppendFile(false);
      setAppendFileList([]);
      setSelectedRadioColumn(DEFAULT_RADIO_COLUMNS);
      setColMappingType(FILE_SHEET_MAPPING_COLUMN_TYPES.MANUAL);
    };

    const saveSheetSelection = async (sheetName, plant_file_id = null) => {
      try {
        setIsLoading(true);
        const response = await uploadSelectedSheet(
          selectedTrain,
          plant_file_id || selectedFileInfo.plant_file_id,
          sheetName,
        );
        setColumnOptions(response?.cols);
        setFileId(response?.file_id);
        dispatch({
          type: "STORE_FILE_ID",
          payload: {
            file_id: response?.file_id,
          },
        });

        const latestPlantResponse = await getPlantDetails(
          plantId,
          true,
          selectedTrain,
        );
        if (latestPlantResponse?.data) {
          const plantData = latestPlantResponse.data;
          const { file, uploaded_files, plant_file_id } = plantData;
          setFileInfo({
            fileName: file?.file_name,
            plant_file_id,
            sheetName: file.sheet_name,
            uploaded_in_blob: file.uploaded_in_blob,
          });
          setUploadedFiles(uploaded_files);
        }
      } catch (error) {
        createNotification(
          ERROR_NOTIFICATION,
          t("notificationStrings.somethingWrong"),
          "",
        );
      } finally {
        setIsLoading(false);
      }
    };

    const handleSheetChange = async (value) => {
      if (value === selectedSheet) return;
      setSelectedSheet(value);
      if (!value) {
        resetState();
        setSelectedSheet(null);
        return;
      }
      setMappedColumn([]);
      setMappedReference(null);
      setSelectedCIPFileName(null);
      setHasRecurrentCIPs(false);
      setIsAppendFile(false);
      setAppendFileList([]);
      setSelectedRadioColumn(DEFAULT_RADIO_COLUMNS);
      setColMappingType(FILE_SHEET_MAPPING_COLUMN_TYPES.MANUAL);

      saveSheetSelection(value);
    };

    useEffect(() => {
      setSelectedFileInfo(fileInfo);

      const { sheetName, plant_file_id, uploaded_in_blob } = fileInfo || {};
      if (sheetName && plant_file_id && !uploaded_in_blob) {
        saveSheetSelection(sheetName, plant_file_id);
      }
    }, [fileInfo]);

    return (
      <div className="ro-card-wrapper">
        <div className="grid-container-row">
          {uploadedFiles.length > 0 && (
            <WPDropdown
              id="selected-file-name"
              label={t("addNewPlant.selFile")}
              options={fileDropdownOptions}
              onChange={(option) => handleFileChange(option?.value)}
              value={selectedFileInfo?.fileName || null}
            />
          )}

          <WPDropdown
            id="selected-sheet-name"
            label={t("addNewPlant.selSheet")}
            options={sheetDropdownOptions}
            onChange={(option) => {
              handleSheetChange(option?.value);
            }}
            value={selectedSheet}
            disabled={!selectedTrain}
          />
        </div>
      </div>
    );
  },
);

export default FileSheetSelection;
